// Máscara e validação paramétrica do número de série.
// Tokens definidos pelo backend: '#' dígito, '@' letra, '*' alfanumérico; demais caracteres são literais.

import type { SerialNumberRules } from '@/types/api';

function tokenMatcher(patternChar: string): RegExp | null {
  if (patternChar === '#') {
    return /\d/;
  }
  if (patternChar === '@') {
    return /[A-Za-z]/;
  }
  if (patternChar === '*') {
    return /[A-Za-z0-9]/;
  }
  return null;
}

export function applyMask(input: string | null, pattern: string): string {
  const raw = (input ?? '').toUpperCase();
  let out = '';
  let inputIndex = 0;

  for (const patternChar of pattern) {
    const matcher = tokenMatcher(patternChar);

    if (matcher) {
      while (inputIndex < raw.length && !matcher.test(raw.charAt(inputIndex))) {
        inputIndex++;
      }
      if (inputIndex >= raw.length) {
        break;
      }
      out += raw.charAt(inputIndex);
      inputIndex++;
    } else {
      out += patternChar;
      if (inputIndex < raw.length && raw.charAt(inputIndex) === patternChar) {
        inputIndex++;
      }
    }
  }

  return out;
}

function patternToRegex(pattern: string): RegExp {
  const parts = pattern.split('').map((c) => {
    if (c === '#') {
      return '\\d';
    }
    if (c === '@') {
      return '[A-Za-z]';
    }
    if (c === '*') {
      return '[A-Za-z0-9]';
    }
    return c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });
  return new RegExp(`^${parts.join('')}$`);
}

export function validateSerial(value: string, rules: SerialNumberRules | null): boolean {
  if (!value) {
    return false;
  }
  if (!rules) {
    return value.length > 3;
  }

  const { pattern, strict, minLength, maxLength } = rules;

  if (strict && pattern) {
    return patternToRegex(pattern).test(value);
  }

  const min = minLength ?? 4;
  const max = maxLength ?? Infinity;
  return value.length >= min && value.length <= max;
}

export function extractLiteralPrefix(pattern: string | null): string {
  if (!pattern) {
    return '';
  }
  const match = pattern.match(/^[^#@*]+/);
  return match ? match[0].toUpperCase() : '';
}

export function describePattern(pattern: string | null): string {
  if (!pattern) {
    return '';
  }
  return pattern
    .split('')
    .map((c) => {
      if (c === '#') {
        return '0';
      }
      if (c === '@') {
        return 'A';
      }
      if (c === '*') {
        return 'X';
      }
      return c;
    })
    .join('')
    .toUpperCase();
}
